import React, { useState } from 'react';
import { ui, HEADER_ACCENT_HOVER } from './Header.styled';
import { Box, Drawer, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import EditLocationAltOutlinedIcon from '@mui/icons-material/EditLocationAltOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUserIsAdmin, selectCurrentUserIsPortalAdmin, selectUserAvatarName, selectUserEmail, selectUserFullName, selectUserOrgName, selectUserRole } from '../../auth/redux/slice';
import { selectOrganization } from '../../organizations/add-edit/redux/slice';
import { ORGANIZATIONS_ACCESS_ROLES, PURCHASE_HISTORY_ACCESS_ROLES, ROUTES, USERS_ACCESS_ROLES, USER_GROUP_ACCESS_ROLES, USER_ROLES } from '../constants';
import { CreateOrderAction } from './CreateOrderAction';
import { OrgSwitcher } from '../../orgSwitcher/components/OrgSwitcher';

export type HeadProps = {
    currentUrl: string;
}

// Supply History and Testing Inventory are both entirely Pre-Buy features (kit orders / kit
// stock) - preBuyOnly hides them for orgs that don't have Pre-Buy enabled, same rule
// RequireOrderIntent now enforces at the route level for these two pages.
//
// Aug 2026 - `PRIMARY_NAV_COUNT` items (whichever apply to this user, in this order) stay
// permanently inline; everything past that lives under "More" - so the header's own width stays
// fixed no matter how many features get added later or which subset a given role/org actually
// sees. This is what actually fixes "what happens if we have all the menus enabled" - previously
// every enabled item competed for the same row, which is what pushed the user's name off the edge
// of the header. Order here is priority order, not just display order - put anything used
// constantly near the top so it's more likely to make the inline cut for every role.
const PRIMARY_NAV_COUNT = 5;
const headerLinks = [
    { label: 'Home',            url: ROUTES.Dashboard,        icon: HomeOutlinedIcon },
    { label: 'Test Submissions', url: ROUTES.Orders,          icon: ReceiptLongOutlinedIcon },
    { label: 'Supply History',  url: ROUTES.KitOrders,        icon: LocalShippingOutlinedIcon, preBuyOnly: true },
    // Sep 2026 - admin-only: the page shows dollar amounts, card last-4 and an org's whole
    // purchase record. PURCHASE_HISTORY_ACCESS_ROLES is its own list rather than USER_ROLES_ADMINS,
    // which omits OrganizationAdminProvider and would hide this from most real org admins.
    { label: 'Purchase History', url: ROUTES.PurchaseHistory, icon: ReceiptLongOutlinedIcon, roles: PURCHASE_HISTORY_ACCESS_ROLES },
    { label: 'Testing Supplies Inventory', url: ROUTES.Kits,  icon: ScienceOutlinedIcon, preBuyOnly: true },
    { label: 'Price Groups',    url: ROUTES.UserGroups,       icon: GroupsOutlinedIcon,    roles: USER_GROUP_ACCESS_ROLES },
    { label: 'Users',           url: ROUTES.Users,            icon: PeopleAltOutlinedIcon, roles: USERS_ACCESS_ROLES },
    { label: 'Users',           url: ROUTES.Organizations,    icon: PeopleAltOutlinedIcon, roles: ORGANIZATIONS_ACCESS_ROLES },
    // Aug 2026 - not Portal-Admin-only (unlike Platform Maintenance, which lives in the user
    // dropdown menu) - any user who can create orders can access this, so it's a regular nav item.
    // Commented out per request - route/page left intact, just hidden from the nav for now.
    /* { label: 'Tracking Maintenance', url: ROUTES.TrackingMaintenance, icon: EditLocationAltOutlinedIcon, preBuyOnly: true }, */
    { label: 'Support',        url: ROUTES.Support,          icon: HelpOutlineOutlinedIcon },
    { label: 'Reports',        url: ROUTES.OrgActivityReports, icon: SummarizeOutlinedIcon },
    /* { label: 'Billing', url: ROUTES.OrgBillingReports }, */
];

const isNavActive = (currentUrl: string, navUrl: string) => {
    if (currentUrl === '/' && navUrl === '/') return true;
    if (navUrl !== '/' && currentUrl.match(navUrl)) return true;
    return false;
};

const HeaderFC: React.FC<HeadProps> = ({ currentUrl }) => {
    const email = useSelector(selectUserEmail);
    const orgName = useSelector(selectUserOrgName);
    const fullName = useSelector(selectUserFullName);
    const avatarInitials = useSelector(selectUserAvatarName);
    const userRole = useSelector(selectUserRole);
    const isPortalAdmin = useSelector(selectCurrentUserIsPortalAdmin);
    const isAdmin = useSelector(selectCurrentUserIsAdmin);
    // Same distinction CreateOrderAction.tsx/RequireOrderIntent.tsx make - Organization Admin(+
    // Provider) belong to a single, known org, so the Pre-Buy nav items are gated on that org's own
    // flag like anyone else. A true multi-org Portal Admin always sees them (per-org enforcement
    // happens on the pages themselves via RequireOrderIntent). selectedOrganization is already
    // fetched by CreateOrderAction (rendered below in this same header) for org-scoped users, so
    // this doesn't need its own fetch.
    const isTrueAdmin = isAdmin && userRole !== USER_ROLES.OrganizationAdminProvider && userRole !== USER_ROLES.OrganizationAdmin;
    const selectedOrganization = useSelector(selectOrganization);
    const preBuyEnabled = isTrueAdmin || Boolean(selectedOrganization?.preBuyEnabled);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

    const visibleLinks = headerLinks.filter(link =>
        (!link.roles || link.roles.includes(userRole!)) && (!link.preBuyOnly || preBuyEnabled)
    );
    const primaryLinks = visibleLinks.slice(0, PRIMARY_NAV_COUNT);
    const secondaryLinks = visibleLinks.slice(PRIMARY_NAV_COUNT);
    const isMoreActive = secondaryLinks.some(link => isNavActive(currentUrl, link.url));

    return (
        <ui.Header>
            <ui.LogoContainer to={ROUTES.Dashboard}>
                <ui.Logo />
            </ui.LogoContainer>

            {/* Desktop navigation - primary items stay inline permanently; everything else lives
                under "More" so this row's width never depends on how many features are enabled. */}
            <ui.NavigationContainer>
                {primaryLinks.map((link, index) => {
                    const Icon = link.icon;
                    return (
                        <ui.NavigationItem key={index}>
                            <ui.NavigationLink
                                selected={isNavActive(currentUrl, link.url)}
                                to={link.url}
                            >
                                <Icon sx={{ fontSize: '15px' }} />
                                <span>{link.label}</span>
                            </ui.NavigationLink>
                        </ui.NavigationItem>
                    );
                })}
                {secondaryLinks.length > 0 && (
                    <ui.NavigationItem>
                        <ui.MoreButton selected={isMoreActive} onClick={e => setMoreAnchor(e.currentTarget)}>
                            <MoreHorizOutlinedIcon sx={{ fontSize: '17px' }} />
                            More
                        </ui.MoreButton>
                        <Menu
                            anchorEl={moreAnchor}
                            open={Boolean(moreAnchor)}
                            onClose={() => setMoreAnchor(null)}
                            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                            PaperProps={{ elevation: 3, sx: { mt: '8px', minWidth: 220, borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.10)' } }}
                        >
                            {secondaryLinks.map((link, index) => {
                                const Icon = link.icon;
                                const active = isNavActive(currentUrl, link.url);
                                return (
                                    <MenuItem key={index} component={RouterLink} to={link.url} onClick={() => setMoreAnchor(null)}
                                        sx={{ px: '16px', py: '10px', gap: '12px', '&:hover': { backgroundColor: '#F0FAFB' } }}>
                                        <ListItemIcon sx={{ minWidth: 'unset' }}>
                                            <Icon sx={{ fontSize: '18px', color: active ? HEADER_ACCENT_HOVER : '#8B99A6' }} />
                                        </ListItemIcon>
                                        <ListItemText primaryTypographyProps={{ fontSize: '13px', fontWeight: active ? 700 : 600, color: active ? HEADER_ACCENT_HOVER : '#1A2332' }}>
                                            {link.label}
                                        </ListItemText>
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                    </ui.NavigationItem>
                )}
            </ui.NavigationContainer>

            {/* Org Switcher - only rendered for User Group Manager with >1 org in their group */}
            <OrgSwitcher />

            {/* Create New Order - moved here from the Orders page so it's reachable everywhere */}
            <CreateOrderAction />
            <ui.ActionsDivider />

            {/* Desktop user button */}
            <ui.UserButton onClick={(e) => setMenuAnchor(e.currentTarget as HTMLElement)} aria-label="User menu">
                <ui.UserAvatar>{avatarInitials}</ui.UserAvatar>
                <ui.UserName>{fullName}</ui.UserName>
                <KeyboardArrowDownIcon sx={{ fontSize: '14px', color: '#9CA3AF', flexShrink: 0 }} />
            </ui.UserButton>

            {/* User dropdown menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{ elevation: 3, sx: { mt: '8px', width: 320, borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.10)' } }}
            >
                <ui.MenuHeader>
                    <ui.MenuAvatar>{avatarInitials}</ui.MenuAvatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <ui.MenuHeaderName>{fullName}</ui.MenuHeaderName>
                        <ui.MenuHeaderMeta>{userRole}</ui.MenuHeaderMeta>
                        <ui.MenuHeaderOrg>{orgName}</ui.MenuHeaderOrg>
                        <ui.MenuHeaderEmail>{email}</ui.MenuHeaderEmail>
                    </Box>
                </ui.MenuHeader>
                {/* Aug 2026 - Test Credit $ balance (TestCreditHelper draws this down against
                    future Pre-Buy charges). Always shown, not just when the org has a balance -
                    null/undefined/empty all default to $0.00 rather than hiding the section or
                    showing something like "$NaN". */}
                <ui.MenuCreditSection>
                    <ui.MenuCreditLabel>
                        <CreditCardOutlinedIcon sx={{ fontSize: '16px', color: HEADER_ACCENT_HOVER }} />
                        Test Credits
                    </ui.MenuCreditLabel>
                    <ui.MenuCreditValue>${(Number(selectedOrganization?.testCreditDollars) || 0).toFixed(2)}</ui.MenuCreditValue>
                </ui.MenuCreditSection>
                <Box sx={{ py: '8px' }}>
                    <MenuItem component={RouterLink} to={ROUTES.Account} onClick={() => setMenuAnchor(null)}
                        sx={{ px: '16px', py: '10px', gap: '12px', '&:hover': { backgroundColor: '#F0FAFB' } }}>
                        <ListItemIcon sx={{ minWidth: 'unset' }}>
                            <Box sx={{
                                width: '30px', height: '30px', borderRadius: '9px',
                                backgroundColor: 'rgba(8,145,178,0.12)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <AccountCircleOutlinedIcon sx={{ fontSize: '17px', color: HEADER_ACCENT_HOVER }} />
                            </Box>
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontSize: '13px', fontWeight: 600, color: '#1A2332', fontFamily: '"Inter", sans-serif' }}>
                            My Account
                        </ListItemText>
                    </MenuItem>
                    {isPortalAdmin && (
                        <MenuItem component={RouterLink} to={ROUTES.PlatformMaintenance} onClick={() => setMenuAnchor(null)}
                            sx={{ px: '16px', py: '10px', gap: '12px', '&:hover': { backgroundColor: '#F0FAFB' } }}>
                            <ListItemIcon sx={{ minWidth: 'unset' }}>
                                <Box sx={{
                                    width: '30px', height: '30px', borderRadius: '9px',
                                    backgroundColor: 'rgba(8,145,178,0.12)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <BuildOutlinedIcon sx={{ fontSize: '17px', color: HEADER_ACCENT_HOVER }} />
                                </Box>
                            </ListItemIcon>
                            <ListItemText primaryTypographyProps={{ fontSize: '13px', fontWeight: 600, color: '#1A2332', fontFamily: '"Inter", sans-serif' }}>
                                Platform Maintenance
                            </ListItemText>
                        </MenuItem>
                    )}
                    <Divider sx={{ mx: '12px', my: '2px', borderColor: '#F1F5F9' }} />
                    <MenuItem component={RouterLink} to={ROUTES.SignOut} onClick={() => setMenuAnchor(null)}
                        sx={{ px: '16px', py: '10px', gap: '12px', '&:hover': { backgroundColor: '#FEF2F2' } }}>
                        <ListItemIcon sx={{ minWidth: 'unset' }}>
                            <Box sx={{
                                width: '30px', height: '30px', borderRadius: '9px',
                                backgroundColor: 'rgba(239,68,68,0.12)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <LogoutOutlinedIcon sx={{ fontSize: '17px', color: '#EF4444' }} />
                            </Box>
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontSize: '13px', fontWeight: 600, color: '#EF4444', fontFamily: '"Inter", sans-serif' }}>
                            Sign Out
                        </ListItemText>
                    </MenuItem>
                </Box>
            </Menu>

            {/* Mobile hamburger */}
            <ui.HamburgerButton
                onClick={() => setDrawerOpen(true)}
                aria-label="Open navigation menu"
            >
                <MenuIcon />
            </ui.HamburgerButton>

            {/* Mobile drawer */}
            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
            >
                <ui.DrawerContent>
                    <ui.DrawerHeader>
                        <ui.Logo style={{ paddingTop: 0, height: '44px', width: 'auto' }} />
                        <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close menu">
                            <CloseIcon />
                        </IconButton>
                    </ui.DrawerHeader>

                    <ui.DrawerUserInfo>
                        <ui.DrawerUserOrg>{orgName}</ui.DrawerUserOrg>
                        <ui.DrawerUserEmail>{email}</ui.DrawerUserEmail>
                    </ui.DrawerUserInfo>

                    <ui.DrawerNavList>
                        {primaryLinks.map((link, index) => (
                            <ui.DrawerNavLink
                                key={index}
                                selected={isNavActive(currentUrl, link.url)}
                                to={link.url}
                                onClick={() => setDrawerOpen(false)}
                            >
                                {link.label}
                            </ui.DrawerNavLink>
                        ))}
                        {secondaryLinks.length > 0 && (
                            <>
                                <ui.DrawerNavSectionLabel>More</ui.DrawerNavSectionLabel>
                                {secondaryLinks.map((link, index) => (
                                    <ui.DrawerNavLink
                                        key={index}
                                        selected={isNavActive(currentUrl, link.url)}
                                        to={link.url}
                                        onClick={() => setDrawerOpen(false)}
                                    >
                                        {link.label}
                                    </ui.DrawerNavLink>
                                ))}
                            </>
                        )}
                    </ui.DrawerNavList>
                </ui.DrawerContent>
            </Drawer>
        </ui.Header>
    );
};

export const Header = React.memo(HeaderFC);
